import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Trophy, Sparkles, Plug, CreditCard, LogOut, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/giveaways", label: "Giveaways", icon: Trophy, testid: "nav-giveaways" },
  { to: "/widgets", label: "Storefront Widgets", icon: LayoutGrid, testid: "nav-widgets" },
  { to: "/success-score", label: "Success Score", icon: Sparkles, testid: "nav-success-score" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="w-60 border-r border-zinc-200 bg-white flex flex-col">
        <div className="px-5 py-5 border-b border-zinc-200">
          <Link to="/dashboard" data-testid="logo-link" className="flex items-center gap-2">
            <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
            <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-100 text-zinc-950 font-medium"
                    : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50"
                }`
              }
            >
              <item.icon className="size-4" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="size-8 rounded-full" />
            ) : (
              <div className="size-8 rounded-full bg-zinc-200" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="text-zinc-400 hover:text-zinc-950 transition-colors"
              title="Sign out"
            >
              <LogOut className="size-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex items-center gap-2 px-2 pt-2">
            <a href="/privacy" data-testid="sidebar-privacy-link" className="text-[11px] text-zinc-400 hover:text-zinc-950 transition-colors">
              Privacy Policy
            </a>
            <span className="text-zinc-300 text-[11px]">·</span>
            <a href="/tos" data-testid="sidebar-tos-link" className="text-[11px] text-zinc-400 hover:text-zinc-950 transition-colors">
              Terms
            </a>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-x-hidden">
        <div className="page-enter">{children}</div>
      </main>
    </div>
  );
}
